import type { AssetDelta, FolderRow, JobRow } from "@loumy/contracts";
import type { QueryClient } from "@tanstack/react-query";
import { api, type AssetsView } from "../api/client";
import { DELTA_CURSOR_KEY, readCacheOwner } from "./persistedSession";
import { qk } from "./queryClient";
import { useUiStore } from "./uiStore";

// §8/D149 — Delta-Sync-Client. Statt bei jedem (Re)Connect/Notice alle Listen blind zu
// invalidieren (Reconnect-Storm aus presign-Fanout-Fetches), holt EIN Aufruf nur die seit dem
// Cursor (updated_at) geänderten Zeilen und merged sie in den (persistierten) Query-Cache.
// Single-flight + Debounce gegen Notice-Bursts. Der Cursor lebt in localStorage; fehlt er
// (Erst-Delta nach Deploy / Storage geleert), wird `since=null` gesendet ⇒ server-seitiger
// Voll-Abzug (gedeckelt, idempotent gemerged) — lückenfrei statt aus einem Cache-Wasserzeichen
// zu raten (Codex-P2). Cross-User-Schutz: `deltaSync(qc, userId)` verwirft eine Antwort, deren
// Session mid-flight wechselte (Codex-P0).

const JOBS_CAP = 50; // wie listJobs
const ASSETS_CAP = 120; // wie listAssets

function readCursor(): string | null {
  try {
    if (typeof localStorage !== "undefined") return localStorage.getItem(DELTA_CURSOR_KEY);
  } catch {
    /* Storage geblockt → since=null ⇒ Voll-Abzug */
  }
  return null;
}
function writeCursor(s: string): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(DELTA_CURSOR_KEY, s);
  } catch {
    /* Storage geblockt → kein Persist (nächster Sync = Voll-Abzug) */
  }
}

const ms = (ts: unknown): number => {
  const t = new Date(ts as string).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/** Geänderte Jobs per id mergen, nach created_at desc sortieren, auf die neuesten 50 deckeln. */
export function mergeJobs(qc: QueryClient, jobs: JobRow[]): void {
  if (jobs.length === 0) return;
  qc.setQueryData<JobRow[]>(qk.jobs, (old) => {
    const map = new Map((old ?? []).map((j) => [j.id, j]));
    for (const j of jobs) map.set(j.id, j);
    return [...map.values()].sort((a, b) => ms(b.created_at) - ms(a.created_at)).slice(0, JOBS_CAP);
  });
}

/** Geänderte Ordner mergen; getrashte (trashed_at != null) als Tombstone ENTFERNEN. */
export function mergeFolders(qc: QueryClient, folders: FolderRow[]): void {
  if (folders.length === 0) return;
  const tombstoned: string[] = [];
  qc.setQueryData<FolderRow[]>(qk.folders, (old) => {
    const map = new Map((old ?? []).map((f) => [f.id, f]));
    for (const f of folders) {
      if (f.trashed_at) {
        map.delete(f.id);
        tombstoned.push(f.id);
      } else map.set(f.id, f);
    }
    return [...map.values()].sort((a, b) => ms(a.created_at) - ms(b.created_at));
  });
  // Codex-R2-P1: ein Folder-Tombstone (z.B. delete_folder, das >120 Assets in EINER Transaktion
  // mit identischem updated_at trasht) wird vom 120er-gedeckelten Asset-Delta nicht zwingend
  // vollständig als Asset-Tombstone abgedeckt → den GANZEN Asset-View des gelöschten Ordners
  // verwerfen (er ist aus der Sicht-Plane raus, §9.4). Steht der gelöschte Ordner als ausgewählt,
  // zurück auf die Inbox. Subtree: delete_folder tombstoned auch alle Unterordner (folders
  // ungedeckelt → alle kommen mit) ⇒ jeder bekommt sein removeQueries.
  if (tombstoned.length === 0) return;
  for (const id of tombstoned) qc.removeQueries({ queryKey: qk.assets(id) });
  const sel = useUiStore.getState().selectedFolderId;
  if (sel && tombstoned.includes(sel)) useUiStore.getState().selectFolder(null);
}

/**
 * Geänderte Assets in JEDEN gecachten Ordner-View einarbeiten: ein Δ-Asset gehört in genau einen
 * Ordner (folder_id) und nur, wenn nicht getrasht → dort upserten (mit frischer presigned URL/
 * Titel/Input-URL); aus ALLEN anderen Ordner-Caches muss es RAUS (Verschiebung/Trash). So bleibt
 * jede gemountete Ordner-Liste konsistent. Ein Ordner ohne Cache-Eintrag wird übersprungen — er
 * zieht beim Öffnen ohnehin frisch (refetchOnMount, frische Presign-URLs).
 */
export function mergeAssets(qc: QueryClient, deltas: AssetDelta[]): void {
  if (deltas.length === 0) return;
  for (const [key, data] of qc.getQueriesData<AssetsView>({ queryKey: ["assets"] })) {
    if (!data) continue;
    const folderKey = (key as readonly unknown[])[1];
    const targetFolderId: string | null = folderKey === "inbox" ? null : (folderKey as string);
    let assets = data.assets;
    let downloadUrls = data.downloadUrls;
    let titles = data.titles;
    let inputImageUrls = data.inputImageUrls;
    let changed = false;
    const fork = () => {
      if (changed) return;
      assets = [...assets];
      downloadUrls = { ...downloadUrls };
      titles = { ...titles };
      inputImageUrls = { ...inputImageUrls };
      changed = true;
    };
    for (const d of deltas) {
      const a = d.asset;
      const belongsHere = !a.trashed_at && (a.folder_id ?? null) === targetFolderId;
      const present = assets.some((x) => x.id === a.id);
      if (belongsHere) {
        fork();
        const j = assets.findIndex((x) => x.id === a.id);
        if (j >= 0) assets[j] = a;
        else assets.unshift(a);
        downloadUrls[a.id] = d.downloadUrl;
        titles[a.id] = d.title;
        inputImageUrls[a.id] = d.inputImageUrl;
      } else if (present) {
        fork();
        const j = assets.findIndex((x) => x.id === a.id);
        if (j >= 0) assets.splice(j, 1);
        delete downloadUrls[a.id];
        delete titles[a.id];
        delete inputImageUrls[a.id];
      }
    }
    if (changed) {
      assets = [...assets].sort((x, y) => ms(y.created_at) - ms(x.created_at)).slice(0, ASSETS_CAP);
      // Codex-P2: nach dem Cap die URL-/Titel-/Input-Maps auf die verbliebenen IDs reduzieren —
      // sonst wachsen sie mit herausgeschnittenen (inkl. kurzlebiger presigned) Einträgen mit.
      const keep = new Set(assets.map((a) => a.id));
      const prune = <T,>(m: Record<string, T>): Record<string, T> =>
        Object.fromEntries(Object.entries(m).filter(([id]) => keep.has(id)));
      qc.setQueryData<AssetsView>(key, {
        assets,
        downloadUrls: prune(downloadUrls),
        titles: prune(titles),
        inputImageUrls: prune(inputImageUrls),
      });
    }
  }
}

/**
 * Offene Job-Detail-Queries (qk.job(id)) der geänderten Jobs invalidieren — nur die GECACHTEN
 * (also tatsächlich geöffneten). So zieht ein offener Job seine frischen Outputs (mit presigned
 * URLs) nach, ohne dass wir blind ALLE Job-Details invalidieren (Subrequest-Budget, D106).
 */
function invalidateOpenJobDetails(qc: QueryClient, jobs: JobRow[]): void {
  for (const j of jobs) {
    if (qc.getQueryData(qk.job(j.id)) !== undefined) void qc.invalidateQueries({ queryKey: qk.job(j.id) });
  }
}

let inFlight: Promise<void> | null = null;
let rerun = false;

/**
 * Ein Delta abrufen + in den Cache mergen + Cursor fortschreiben. `userId` = der erwartete
 * Cache-Owner; wechselt die Session WÄHREND des Requests (Logout/anderer User), wird die Antwort
 * verworfen (Codex-P0). Single-flight: läuft schon einer, wird genau EIN weiterer Lauf nachgezogen
 * (Notice-Bursts kollabieren). Best-effort — ein Fehler (offline o.ä.) wird verschluckt, der
 * nächste Notice/Reconnect versucht es erneut.
 */
export function deltaSync(qc: QueryClient, userId: string): Promise<void> {
  if (inFlight) {
    rerun = true;
    return inFlight;
  }
  inFlight = (async () => {
    try {
      const res = await api.delta(readCursor());
      // Codex-P0: Session mid-flight gewechselt (Logout/anderer User)? Dann NICHT mergen und den
      // Cursor NICHT fortschreiben — sonst Bleed in den (gepurgten) Cache des neuen Users.
      if (readCacheOwner() !== userId) return;
      mergeJobs(qc, res.jobs);
      mergeFolders(qc, res.folders);
      mergeAssets(qc, res.assets);
      qc.setQueryData(qk.credits, res.credits);
      invalidateOpenJobDetails(qc, res.jobs);
      // D155: haben sich Jobs geändert (z.B. ein Job wurde FERTIG), die presignten Jobs-Vorschau-
      // Bilder neu ziehen — so erscheint das Thumbnail eines gerade fertig gewordenen Jobs. EIN
      // gedeckelter Refetch (50), nur wenn die Liste auch gemountet ist (aktive Query).
      if (res.jobs.length > 0) void qc.invalidateQueries({ queryKey: qk.jobPreviews });
      writeCursor(res.nextSince);
    } catch {
      /* best-effort: nächster Notice/Reconnect synct erneut */
    } finally {
      inFlight = null;
      if (rerun) {
        rerun = false;
        void deltaSync(qc, userId);
      }
    }
  })();
  return inFlight;
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

/** Delta-Sync gedrosselt anstoßen (Status-Notice-Bursts → ein Abgleich statt N). */
export function scheduleDeltaSync(qc: QueryClient, userId: string, delayMs = 250): void {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void deltaSync(qc, userId);
  }, delayMs);
}
